import { observer } from 'mobx-react-lite'
import {
  ModalDialog,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { SpousalProfileEditor } from '@/features/tax-profile/individual-profiles/components/SpousalProfileEditor'
import type { SpousalProfileEditorViewModel } from '@/features/tax-profile/individual-profiles/SpousalProfileEditorViewModel'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import { useId } from 'react'

export interface SpousalProfileEditorDialogProps {
  vm: SpousalProfileEditorViewModel
  isDisabled?: boolean
  testId?: string
  submitButtonTestId?: string
  cancelButtonTestId?: string
}

/**
 * Dialog for editing a spousal profile.
 */
export const SpousalProfileEditorDialog = observer(
  function SpousalProfileEditorDialog({
    vm,
    isDisabled,
    testId,
    submitButtonTestId,
    cancelButtonTestId,
  }: SpousalProfileEditorDialogProps) {
    const formId = useId()
    const isFormDisabled = vm.save.pending || vm.isReadOnly || isDisabled
    return (
      <ModalDialog
        isOpen={vm.modal.showing}
        onClose={vm.close}
        isDismissable={!vm.save.pending}
        sheetSizing="stretch"
        isUsingDialogScrolling
        size="lg"
        testId={testId}
      >
        <ModalDialogHeader>
          {vm.isReadOnly ? (
            <>
              <ModalDialogTitle>Spouse's details</ModalDialogTitle>
              <ModalDialogDescription>
                View your spouse's details below.
              </ModalDialogDescription>
            </>
          ) : vm.isNew ? (
            <>
              <ModalDialogTitle>Add spouse's details</ModalDialogTitle>
              <ModalDialogDescription>
                Please complete the following so we can confirm your spouse's
                information.
              </ModalDialogDescription>
            </>
          ) : (
            <>
              <ModalDialogTitle>Edit spouse's details</ModalDialogTitle>
              <ModalDialogDescription>
                View your spouse's details below, and make changes if needed.
              </ModalDialogDescription>
            </>
          )}
        </ModalDialogHeader>

        <ModalDialogContent>
          <SpousalProfileEditor
            formId={formId}
            isDisabled={isFormDisabled}
            vm={vm}
          />
        </ModalDialogContent>
        <ModalDialogFooter sticky bordered compact>
          <ButtonGroup stretch>
            <Button
              isDisabled={vm.save.pending}
              variant="outline"
              label={vm.isReadOnly ? 'Close' : 'Cancel'}
              onPress={vm.close}
              testId={cancelButtonTestId}
            />
            {!vm.isReadOnly && (
              <Button
                isPending={vm.save.pending}
                isDisabled={isDisabled}
                variant="default"
                label={vm.isNew ? 'Save' : 'Save changes'}
                type="submit"
                form={formId}
                testId={submitButtonTestId}
              />
            )}
          </ButtonGroup>
        </ModalDialogFooter>
      </ModalDialog>
    )
  },
)
